import { create } from "zustand";

import type { ActiveResponseCompletionHandler } from "@memorang/ui/components/StudySession/types";

/**
 * Tracks which items are completed, to control Next/auto-advance in the session.
 * Items remain completed after revisit. Reset between sessions.
 */
type ItemId = string | number;

type ResponseControlsState = {
	completedItems: Record<string, boolean>;
	audioCompletedItems: Record<string, number>;
	recordingActiveItems: Record<string, boolean>;
	activeCompletionHandlers: Record<string, ActiveResponseCompletionHandler>;
	markResponseComplete: (itemId: ItemId) => void;
	markAudioComplete: (itemId: ItemId) => void;
	setRecordingActive: (itemId: ItemId, active: boolean) => void;
	registerActiveResponseCompletion: (
		itemId: ItemId,
		handler: ActiveResponseCompletionHandler,
	) => () => void;
	completeActiveResponse: (itemId: ItemId) => Promise<void>;
	reset: () => void;
};

const useResponseControlsStore = create<ResponseControlsState>()(
	(set, get) => ({
		completedItems: {},
		audioCompletedItems: {},
		recordingActiveItems: {},
		activeCompletionHandlers: {},
		markResponseComplete: (itemId: ItemId) =>
			set((state) =>
				state.completedItems[itemId]
					? state
					: { completedItems: { ...state.completedItems, [itemId]: true } },
			),
		markAudioComplete: (itemId: ItemId) =>
			set((state) =>
				state.audioCompletedItems[itemId]
					? state
					: {
							audioCompletedItems: {
								...state.audioCompletedItems,
								[itemId]: Date.now(),
							},
						},
			),
		setRecordingActive: (itemId: ItemId, active: boolean) =>
			set((state) =>
				Boolean(state.recordingActiveItems[itemId]) === active
					? state
					: {
							recordingActiveItems: {
								...state.recordingActiveItems,
								[itemId]: active,
							},
						},
			),
		registerActiveResponseCompletion: (itemId, handler) => {
			const key = String(itemId);
			set((state) => ({
				activeCompletionHandlers: {
					...state.activeCompletionHandlers,
					[key]: handler,
				},
			}));
			return () => {
				set((state) => {
					if (state.activeCompletionHandlers[key] !== handler) {
						return state;
					}
					const { [key]: _removed, ...activeCompletionHandlers } =
						state.activeCompletionHandlers;
					return { activeCompletionHandlers };
				});
			};
		},
		completeActiveResponse: async (itemId) => {
			const handler = get().activeCompletionHandlers[String(itemId)];
			await handler?.();
		},
		reset: () =>
			set({
				completedItems: {},
				audioCompletedItems: {},
				recordingActiveItems: {},
				activeCompletionHandlers: {},
			}),
	}),
);

export default useResponseControlsStore;
